import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Edit, Trash2 } from 'lucide-react';
import CarTypeDialog from '@/components/admin/property/CarTypeDialog';
import { formatCurrency } from '@/utils/format';

export default function CarTypesTab({
  carTypes,
  cars,
  showCarTypeDialog,
  setShowCarTypeDialog,
  editingCarType,
  setEditingCarType,
  carTypeForm,
  setCarTypeForm,
  resetCarTypeForm,
  handleCreateCarType,
  handleUpdateCarType,
  openEditCarType,
  handleDeleteCarType
}) {
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Manage Car Types</h2>
          <p className="text-sm text-gray-600 mt-1">{carTypes.length} car type{carTypes.length !== 1 ? 's' : ''} total</p>
        </div>
        <CarTypeDialog
          showCarTypeDialog={showCarTypeDialog}
          setShowCarTypeDialog={setShowCarTypeDialog}
          editingCarType={editingCarType}
          setEditingCarType={setEditingCarType}
          carTypeForm={carTypeForm}
          setCarTypeForm={setCarTypeForm}
          resetCarTypeForm={resetCarTypeForm}
          handleCreateCarType={handleCreateCarType}
          handleUpdateCarType={handleUpdateCarType}
        />
      </div>

      {/* Car Types Table */}
      <Card className="border-0 shadow-lg">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Type Name</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Description</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Base Daily Rate</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Cars Using</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {carTypes.map((carType) => {
                  const carsUsingType = cars.filter(c => c.car_type_id === carType.id).length;
                  return (
                    <tr key={carType.id} className="hover:bg-gray-50" data-testid={`car-type-row-${carType.id}`}>
                      <td className="px-6 py-4">
                        <div className="text-sm font-semibold text-gray-900">{carType.name}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-600">{carType.description}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{formatCurrency(carType.base_daily_rate)}/day</div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-700">
                          {carsUsingType} car{carsUsingType !== 1 ? 's' : ''}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="sm" onClick={() => openEditCarType(carType)} data-testid={`edit-car-type-${carType.id}`}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleDeleteCarType(carType.id)} 
                            disabled={carsUsingType > 0}
                            data-testid={`delete-car-type-${carType.id}`}
                          >
                            <Trash2 className={`w-4 h-4 ${carsUsingType > 0 ? 'text-gray-300' : 'text-red-500'}`} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {carTypes.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <p>No car types added yet. Click "Add Car Type" to get started.</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
